import React, { useEffect, useMemo } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { Loader } from '@crossfit/ui'

import DailyClassPlanPage from './DailyClassPlan'
import WeeklyOverviewPage from './WeeklyOverview'
import PDFsPage from './PDFsPage'
import { UnitPreferenceProvider } from './components/UnitPreference'
import SorryCoachPage from './SorryCoach'
import WelcomeFormModal from './WelcomeFormModal'
import { AffiliateStandingIssueNotificationBanner } from './components/NotificationBanner'
import {
  SubscriptionPackageType,
  SubscriptionStatus,
} from 'services/programming'
import { useAuth } from 'components/Auth'
import { useMyActiveSubscriptions } from 'hooks/subscriptions'
import { useAffiliate } from 'hooks/useAffiliate'

function ProgrammingNotificationBanner({ hasAffiliateStandingIssue }) {
  if (hasAffiliateStandingIssue) {
    return <AffiliateStandingIssueNotificationBanner />
  }

  return null
}

// TODO: Maybe remove this component entirely
function RequireSubscription({ children, hasAffiliateStandingIssue, isCoach }) {
  // TODO: Update logic for Membership status to account for CAP bundling and redirect to homepage instead of signup when there is not active subscription

  return (
    <>
      {!isCoach && (
        <ProgrammingNotificationBanner
          hasAffiliateStandingIssue={hasAffiliateStandingIssue}
        />
      )}
      {children}
    </>
  )
}

function ProgrammingPage() {
  const { pathname } = useLocation()
  const { isLoading } = useAuth()
  const { selectedAffiliate: affiliate, isCoach } = useAffiliate()
  const subscriptions = useMyActiveSubscriptions()
  const hasAffiliateStandingIssue =
    !isCoach &&
    affiliate?.affiliateId &&
    affiliate?.financialStatus !== 'Current'
  const needsToCompleteWelcomeForm = useMemo(() => {
    return (
      subscriptions
        .filter(
          (s) =>
            s.status === SubscriptionStatus.active &&
            s.package.name === SubscriptionPackageType.ATK,
        )
        .every((ss) => !ss.requirementsMet) && affiliate?.id != null
    )
  }, [subscriptions, affiliate])

  useEffect(() => {
    scrollTo(0, 0)
  }, [pathname])

  if (isLoading) {
    return <Loader />
  }

  return (
    <UnitPreferenceProvider>
      {needsToCompleteWelcomeForm && <WelcomeFormModal isCoach={isCoach} />}

      <Routes>
        <Route
          index
          element={
            <RequireSubscription
              isCoach={isCoach}
              hasAffiliateStandingIssue={hasAffiliateStandingIssue}
            >
              <WeeklyOverviewPage />
            </RequireSubscription>
          }
        />
        <Route
          path='pdfs'
          element={
            <RequireSubscription
              isCoach={isCoach}
              hasAffiliateStandingIssue={hasAffiliateStandingIssue}
            >
              <PDFsPage />
            </RequireSubscription>
          }
        />
        <Route path='sorry-coach' element={<SorryCoachPage />} />
        <Route
          path=':track/weekly-overview/:dateSlug'
          element={
            <RequireSubscription
              isCoach={isCoach}
              hasAffiliateStandingIssue={hasAffiliateStandingIssue}
            >
              <WeeklyOverviewPage />
            </RequireSubscription>
          }
        />
        <Route
          path=':track/daily-class-plan/:date'
          element={
            <RequireSubscription
              isCoach={isCoach}
              hasAffiliateStandingIssue={hasAffiliateStandingIssue}
            >
              <DailyClassPlanPage />
            </RequireSubscription>
          }
        />
        <Route path='*' element={<Navigate to='/' />} />
      </Routes>
    </UnitPreferenceProvider>
  )
}

export default ProgrammingPage
